import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MessageSquareText, Truck } from 'lucide-react';
import { api } from '../../lib/api';
import { outboxApi, whenShort, OUTBOX_TONE, type OutboxItem } from '../../lib/comms';
import { useCan } from '../../hooks/usePermission';
import { Card, Badge, Button, Textarea } from '../ui';
import { FormError } from '../forms/common';
import { formatPhone } from '../../lib/phone';

type WO = Record<string, any>;

/**
 * Letters and texts from one job (Task S): send the work order to its contractor, tell the resident how it is going
 * (automatically on each status change while "keep the resident posted" is on, or a text now), how to get in.
 */
export function LettersCard({ wo, onChanged }: { wo: WO; onChanged: () => void }) {
  const canAssign = useCan('workOrders.assign');
  const canUpdate = useCan('workOrders.update');
  const canOutbox = useCan('outbox.read');
  const [access, setAccess] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const letters = useQuery({
    queryKey: ['/outbox', 'work_order', wo.id], enabled: canOutbox,
    queryFn: () => outboxApi.list({ relatedType: 'work_order', relatedId: wo.id, perPage: 6 }),
  });
  const after = (msg: string) => { setDone(msg); onChanged(); void letters.refetch(); };
  const send = useMutation({
    mutationFn: async () => (await api.post<{ outbox: OutboxItem; sent: boolean }>(`/work-orders/${wo.id}/send-to-contractor`, {})).data,
    onSuccess: (r) => after(r.sent ? `Sent to ${r.outbox.toName ?? r.outbox.toEmail}.` : `Saved as a ${r.outbox.status.toLowerCase()} in the outbox — ${r.outbox.error ?? 'finish it there'}.`),
  });
  const text = useMutation({
    mutationFn: async (onTheWay: boolean) => (await api.post<OutboxItem[]>(`/work-orders/${wo.id}/text-resident`, { onTheWay })).data,
    onSuccess: (r) => after(r.every((x) => x.status === 'SENT') ? `Texted ${r.map((x) => formatPhone(x.toPhone)).join(', ')}.` : `Not sent: ${r.find((x) => x.status !== 'SENT')?.error ?? 'see the outbox'}`),
  });
  const patch = useMutation({
    mutationFn: async (body: { notifyResident?: boolean; accessNotes?: string | null }) => api.patch(`/work-orders/${wo.id}`, body),
    onSuccess: () => { setAccess(null); onChanged(); },
  });
  const err = send.error ?? text.error ?? patch.error;
  const open = wo.isOpen;

  return (
    <Card title="Contractor & resident">
      <div className="space-y-3 text-sm">
        {canAssign && open && (
          <div>
            <Button icon="Send" className="w-full" disabled={!wo.vendorId || send.isPending} onClick={() => send.mutate()}
              title={wo.vendorId ? '' : 'Assign a contractor first'}>
              {send.isPending ? 'Sending…' : wo.vendorName ? `Send to ${wo.vendorName}` : 'Send to contractor'}
            </Button>
            <p className="mt-1 text-[11.5px] text-ink-400">{wo.vendorId ? 'Emails the job, the address, how to get in and the booked time.' : 'Assign a contractor to send them the job.'}</p>
          </div>
        )}

        <div className="rounded-lg border border-ink-100 p-2.5">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">Getting in</div>
          {access === null ? (
            <div className="flex items-start gap-2">
              <p className="flex-1 whitespace-pre-wrap text-[12.5px] text-ink-700">{wo.accessNotes || <span className="text-ink-400">No access notes — the contractor is asked to call us.</span>}</p>
              {canUpdate && open && <button onClick={() => setAccess(wo.accessNotes ?? '')} className="text-[11.5px] font-medium text-bronze-600 hover:underline">Edit</button>}
            </div>
          ) : (
            <div className="space-y-1.5">
              <Textarea rows={2} value={access} onChange={(e) => setAccess(e.target.value)} className="w-full" placeholder="Weekdays after 5pm, key with the concierge…" />
              <div className="flex gap-1.5"><Button size="sm" onClick={() => patch.mutate({ accessNotes: access.trim() || null })} disabled={patch.isPending}>Save</Button><Button size="sm" variant="ghost" onClick={() => setAccess(null)}>Cancel</Button></div>
            </div>
          )}
        </div>

        {canUpdate && (
          <label className="flex cursor-pointer items-start gap-2 text-[12.5px] text-ink-700">
            <input type="checkbox" className="mt-0.5" checked={!!wo.notifyResident} disabled={patch.isPending || !open} onChange={(e) => patch.mutate({ notifyResident: e.target.checked })} />
            <span>Keep the resident posted — a repair update is written to them each time the job changes status.</span>
          </label>
        )}
        {canUpdate && open && (
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="secondary" disabled={text.isPending} onClick={() => text.mutate(false)}><MessageSquareText className="h-3.5 w-3.5" />Text an update</Button>
            <Button size="sm" variant="secondary" disabled={text.isPending} onClick={() => text.mutate(true)}><Truck className="h-3.5 w-3.5" />On the way now</Button>
          </div>
        )}
        {done && <p className="rounded-lg bg-emerald-50 px-2.5 py-1.5 text-[12px] text-emerald-800">{done}</p>}
        {err && <FormError error={err} />}

        {canOutbox && (letters.data?.items.length ?? 0) > 0 && (
          <div className="border-t border-ink-100 pt-2">
            <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">Written from this job</div>
            {letters.data!.items.map((l) => (
              <Link key={l.id} to={`/app/outbox?item=${l.id}`} className="flex items-center gap-2 py-1 text-[12px] hover:bg-ink-50">
                <Badge value={l.status} tone={OUTBOX_TONE[l.status]} />
                <span className="min-w-0 flex-1 truncate text-ink-700">{l.channel === 'SMS' ? 'Text' : l.templateKey === 'work_order_to_contractor' ? 'To contractor' : 'To resident'} · {l.toName ?? l.toEmail ?? formatPhone(l.toPhone)}</span>
                <span className="shrink-0 text-ink-400">{whenShort(l.createdAt)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
